import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  forwardRef,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { IconComponent, type IconName } from '../../../icons/icon.component';
import { ToasterService } from '../../../../core/services/toaster.service';
import { TooltipComponent } from "../tooltip/tooltip.component";

export type InputType =
  | 'textarea'
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'url'
  | 'search';
export type InputSize = 'sm' | 'md' | 'lg';
export type textareaRows = 10;
export type InputVariant = 'default' | 'outline' | 'filled' | 'custom';

@Component({
  selector: 'app-textarea',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, TooltipComponent],
  templateUrl: './textarea.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
})
export class TextareaComponent {

  toasterService = inject(ToasterService)

  @Input() type: InputType = 'text';
  @Input() textValue: string = '';
  @Input() rows: number = 10;
  @Input() placeholder = '';
  @Input() name = '';
  @Input() id = '';
  @Input() autocomplete?: string;
  @Input() required = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() maxlength?: number;
  @Input() minlength?: number;
  @Input() pattern?: string;
  @Input() showCopyIcon?: boolean;

  // Appearance
  @Input() size: InputSize = 'md';
  @Input() variant: InputVariant = 'default';
  @Input() fullWidth = true;

  // Icons
  @Input() leftIcon?: IconName;
  @Input() rightIcon?: IconName;
  @Input() showPasswordToggle = true; // Only shows for password type

  // Label and Validation
  @Input() label?: string;
  @Input() hint?: string;
  @Input() error?: string;
  @Input() showErrorIndicator = true;

  // Custom Classes
  @Input() inputClass?: string;
  @Input() containerClass?: string;
  @Input() labelClass?: string;

  // Events
  @Output() valueChange = new EventEmitter<string>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();

  // Internal State
  value = signal('');
  showPassword = signal(false);
  isFocused = signal(false);

  // ControlValueAccessor
  private onChange = (value: any) => { };
  private onTouched = () => { };

  // Public Methods
  togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }


  copyPrompt() {
    navigator.clipboard.writeText(this.value()).then(() => this.toasterService.success('Prompt copied!', '', 2000)).catch(() => this.toasterService.error('Failed to copy prompt'))
  }

  onFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    this.inputFocus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.isFocused.set(false);
    this.onTouched();
    this.inputBlur.emit(event);
  }

  // ControlValueAccessor Implementation
  writeValue(value: any): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Computed Properties
  get inputType(): string {
    if (this.type === 'password' && this.showPassword()) {
      return 'text';
    }
    return this.type;
  }

  get showPasswordIcon(): boolean {
    return this.type === 'password' && this.showPasswordToggle;
  }

  get passwordToggleIcon(): IconName {
    return this.showPassword() ? 'eyeOff' : 'eye';
  }

  // Styling Methods
  getContainerClasses(): string {
    const baseClasses = 'relative h-full';
    const widthClasses = this.fullWidth ? 'w-full' : '';
    const customClasses = this.containerClass || '';

    return [baseClasses, widthClasses, customClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getLabelClasses(): string {
    const baseClasses = 'block text-sm font-medium';
    const colorClasses = 'text-slate-700 dark:text-slate-300';
    const customClasses = this.labelClass || '';

    return [baseClasses, colorClasses, customClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getInputClasses(): string {
    const baseClasses =
      'w-full rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 h-full resize-none';

    // Size classes
    const sizeClasses = this.getSizeClasses();

    // Variant classes
    const variantClasses = this.getVariantClasses();

    // Icon padding
    const leftPadding = this.leftIcon ? 'pl-11' : 'pl-4';
    const rightPadding =
      this.rightIcon || this.showPasswordIcon ? 'pr-11' : 'pr-4';
    const paddingClasses = `${leftPadding} ${rightPadding}`;

    // State classes
    const disabledClasses = this.disabled
      ? 'opacity-50 cursor-not-allowed'
      : '';

    const errorClasses = this.error
      ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20'
      : '';

    const customClasses = this.inputClass || '';

    return [
      baseClasses,
      sizeClasses,
      variantClasses,
      paddingClasses,
      disabledClasses,
      errorClasses,
      customClasses,
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  private getSizeClasses(): string {
    const sizeMap = {
      sm: 'py-2 text-sm',
      md: 'py-3 text-base',
      lg: 'py-4 text-lg',
    };
    return sizeMap[this.size];
  }

  private getVariantClasses(): string {
    const variantMap = {
      default: `bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 
                focus:border-purple-400 focus:ring-purple-500/20
                dark:bg-slate-900 dark:border-purple-500/20 dark:text-white 
                dark:placeholder:text-slate-500 dark:focus:border-purple-500/50`,
      outline: `bg-transparent border-2 border-slate-300 text-slate-900 placeholder:text-slate-400
                focus:border-purple-500 focus:ring-purple-500/20
                dark:border-slate-600 dark:text-white dark:placeholder:text-slate-500 
                dark:focus:border-purple-400`,
      filled: `bg-slate-100 border-2 border-transparent text-slate-900 placeholder:text-slate-400
               focus:border-purple-500 focus:bg-white focus:ring-purple-500/20
               dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 
               dark:focus:border-purple-400 dark:focus:bg-slate-700`,
      custom: `bg-[#FFFFFF] border border-[#BFBFC7] dark:border-ink-subtle text-[#141437] placeholder:text-[#BFBFC7] 
               focus:border-purple-500 focus:bg-white focus:ring-purple-500/20
               dark:bg-surface-1 dark:text-white dark:placeholder:text-ink-subtle 
               dark:focus:border-purple-400 dark:focus:bg-surface-1`,
    };
    return variantMap[this.variant];
  }

  getIconWrapperClasses(position: 'left' | 'right'): string {
    const baseClasses = 'absolute top-1/2 -translate-y-1/2 pointer-events-none';
    const positionClasses = position === 'left' ? 'left-4' : 'right-4';
    const colorClasses = this.error
      ? 'text-red-500 dark:text-red-400'
      : 'text-slate-400 dark:text-slate-500';

    return [baseClasses, positionClasses, colorClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getPasswordToggleClasses(): string {
    const baseClasses =
      'absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer transition-colors duration-200';
    const colorClasses =
      'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300';

    return [baseClasses, colorClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getHintClasses(): string {
    return 'mt-1.5 text-xs text-slate-500 dark:text-slate-400';
  }

  getErrorClasses(): string {
    return 'mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1';
  }
}
